import { useState } from 'react';
import {
  ShareTooLargeError,
  encodeShareState,
  formatMoney,
  formatShareMessage,
  shareLabel,
  type Item,
  type PersonId,
  type Settlement,
} from '@bill/core';
import { Avatar } from './Avatar.jsx';
import { Icon } from './Icon.jsx';

interface CardsProps {
  settlement: Settlement;
  items: Item[];
  speakerId: PersonId;
  extrasRule: 'proportional' | 'equal';
  onExtrasRule(rule: 'proportional' | 'equal'): void;
}

/**
 * Per-person cards plus the tax/tip sharing toggle. Trusted to the cent.
 *
 * Every card is a small ledger: one line per item with the share, the full
 * price muted and this person's amount, then a footer with items, tax and
 * extras. Everything is on screen; nothing to tap.
 */
export function SplitCards({ settlement, items, speakerId, extrasRule, onExtrasRule }: CardsProps): React.ReactElement {
  const hasExtras = settlement.lines.some((line) => line.tax || line.tip || line.other);
  const { currency } = settlement;
  const nameOf = (id: string): string => items.find((item) => item.id === id)?.name ?? id;
  const bare = (amount: number): string => (amount / 100).toFixed(2);
  return (
    <>
      <ul className="cards">
        {settlement.lines.map((line) => {
          const footer = [
            `items ${bare(line.itemsSubtotal)}`,
            line.tax ? `tax ${bare(line.tax)}` : null,
            line.tip ? `tip ${bare(line.tip)}` : null,
            line.other ? `extras ${bare(line.other)}` : null,
          ].filter(Boolean);
          return (
            <li key={line.personId} className={`card${line.personId === speakerId ? ' me' : ''}`}>
              <div className="card-head">
                <Avatar name={line.name} me={line.personId === speakerId} />
                <span className="card-name">{line.name}</span>
                <span className="card-total">{formatMoney(line.total, currency)}</span>
              </div>
              {line.portions.length > 0 && (
                <ul className="ledger">
                  {line.portions.map((portion) => (
                    <li key={portion.itemId}>
                      <span className="share">{shareLabel(portion.weight, portion.weightTotal)}</span>
                      <span className="what">{nameOf(portion.itemId)}</span>
                      {portion.weight < portion.weightTotal && (
                        <span className="full muted">{bare(items.find((item) => item.id === portion.itemId)?.lineTotal ?? 0)}</span>
                      )}
                      <span className="mine">{bare(portion.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {(hasExtras || line.portions.length > 1) && <p className="ledger-foot muted">{footer.join(' · ')}</p>}
            </li>
          );
        })}
      </ul>
      {hasExtras && (
        <div className="segmented" role="radiogroup" aria-label="How to share tax, tip and extras">
          <button type="button" role="radio" aria-checked={extrasRule === 'proportional'} className={extrasRule === 'proportional' ? 'on' : ''} onClick={() => onExtrasRule('proportional')}>
            Extras by what you ate
          </button>
          <button type="button" role="radio" aria-checked={extrasRule === 'equal'} className={extrasRule === 'equal' ? 'on' : ''} onClick={() => onExtrasRule('equal')}>
            Extras per head
          </button>
        </div>
      )}
    </>
  );
}

/** Share as a message (the thing that gets pasted into the group chat) or as a link. */
export function ShareBar({ settlement, items, speakerId }: { settlement: Settlement; items: Item[]; speakerId: PersonId }): React.ReactElement {
  const [note, setNote] = useState<string | null>(null);
  // In the app the speaker is "You"; in a message they send, that reads as "Me".
  const outgoing: Settlement = {
    ...settlement,
    lines: settlement.lines.map((line) => (line.personId === speakerId && line.name === 'You' ? { ...line, name: 'Me' } : line)),
  };

  const flash = (text: string): void => {
    setNote(text);
    window.setTimeout(() => setNote(null), 2000);
  };

  const shareMessage = async (): Promise<void> => {
    const text = formatShareMessage(outgoing, { includeBreakdown: true, items, appLink: `${window.location.origin}/` });
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch {
        // user cancelled, or share sheet unavailable — fall through to copy
      }
    }
    await navigator.clipboard.writeText(text);
    flash('Message copied');
  };

  const copyLink = async (): Promise<void> => {
    try {
      const url = `${window.location.origin}/s#${await encodeShareState(outgoing, items)}`;
      if (navigator.share) {
        try {
          await navigator.share({ url });
          return;
        } catch {
          // fall through
        }
      }
      await navigator.clipboard.writeText(url);
      flash('Link copied');
    } catch (error) {
      flash(error instanceof ShareTooLargeError ? 'Too many items for a link — share the message instead' : 'Could not make a link');
    }
  };

  return (
    <div className="share">
      <div className="share-row">
        <button type="button" className="button primary big" onClick={() => void shareMessage()}>
          <Icon name="share" /> Share
        </button>
        <button type="button" className="button big" onClick={() => void copyLink()}>
          <Icon name="link" /> Copy link
        </button>
      </div>
      {note && <p className="actions-note">{note}</p>}
    </div>
  );
}
